import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Search, MapPin } from 'lucide-react';
import heroImage from '@/assets/hero-runners.jpg';
import { usePublishedEvents } from '@/hooks/useEvents';
import { formatPrice } from '@/lib/price-utils';
import { calculatePricing } from '@/lib/pricing';
import {
  parseSearchIntent,
  buildExplorerUrl,
  eventMatchesIntents,
  hasAnyIntent,
} from '@/lib/search-intent';

const QUICK_FILTERS = [
  { label: 'Ce week-end', to: '/explorer?when=weekend' },
  { label: 'Trail < 25km', to: '/explorer?terrains=trail&max_km=25' },
  { label: 'Marathon', to: '/explorer?distances=marathon' },
  { label: '< 20€', to: '/explorer?max_price=20' },
  { label: 'Débutant', to: '/explorer?difficulties=easy' },
];

const TYPEWRITER_PHRASES = [
  'Chamonix',
  'Trail < 25km',
  'Marathon de Paris',
  '10K ce week-end',
  'Ultra en montagne',
  'Course < 20€',
];

function useTypewriterPlaceholder(phrases: string[], paused: boolean) {
  const [text, setText] = useState('');
  const [reducedMotion, setReducedMotion] = useState(false);
  const indexRef = useRef(0);
  const charRef = useRef(0);
  const phaseRef = useRef<'typing' | 'pausing' | 'deleting'>('typing');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReducedMotion(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      setText(phrases[0] ?? '');
      return;
    }
    if (paused) return;

    let timer: ReturnType<typeof setTimeout>;

    const tick = () => {
      const current = phrases[indexRef.current % phrases.length];
      const phase = phaseRef.current;

      if (phase === 'typing') {
        const next = current.slice(0, charRef.current + 1);
        setText(next);
        charRef.current += 1;
        if (charRef.current >= current.length) {
          phaseRef.current = 'pausing';
          timer = setTimeout(tick, 1400);
        } else {
          timer = setTimeout(tick, 70);
        }
      } else if (phase === 'pausing') {
        phaseRef.current = 'deleting';
        timer = setTimeout(tick, 40);
      } else {
        const next = current.slice(0, Math.max(0, charRef.current - 1));
        setText(next);
        charRef.current = Math.max(0, charRef.current - 1);
        if (charRef.current === 0) {
          indexRef.current = (indexRef.current + 1) % phrases.length;
          phaseRef.current = 'typing';
          timer = setTimeout(tick, 350);
        } else {
          timer = setTimeout(tick, 40);
        }
      }
    };

    timer = setTimeout(tick, 400);
    return () => clearTimeout(timer);
  }, [phrases, paused, reducedMotion]);

  return reducedMotion ? phrases[0] ?? '' : `${text}▍`;
}

const MONTHS_SHORT = ['JAN', 'FÉV', 'MAR', 'AVR', 'MAI', 'JUIN', 'JUIL', 'AOÛT', 'SEPT', 'OCT', 'NOV', 'DÉC'];

function formatShortDate(iso: string) {
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

function daysUntil(iso: string) {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

function normalize(s: string) {
  return s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
}

function Highlight({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const nText = normalize(text);
  const nQuery = normalize(query);
  const idx = nText.indexOf(nQuery);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-primary/25 text-foreground rounded-sm px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

const HeroSection = () => {
  const { data: events = [] } = usePublishedEvents();
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  const animatedPlaceholder = useTypewriterPlaceholder(
    TYPEWRITER_PHRASES,
    isFocused || query.length > 0,
  );

  const trimmedQuery = query.trim();

  const parsedSearch = useMemo(() => parseSearchIntent(trimmedQuery), [trimmedQuery]);
  const intentActive = hasAnyIntent(parsedSearch.filters);
  const residual = parsedSearch.residualText;

  const { suggestions, totalMatches } = useMemo(() => {
    if (!trimmedQuery) return { suggestions: [] as any[], totalMatches: 0 };
    const now = Date.now();
    const nq = residual ? normalize(residual) : '';
    const scored = events
      .filter((e: any) => new Date(e.event_date).getTime() > now)
      .filter((e: any) => eventMatchesIntents(e, parsedSearch.filters))
      .map((e: any) => {
        let score = 0;
        if (nq) {
          const nTitle = normalize(e.title || '');
          const nCity = normalize(e.city || '');
          const nLoc = normalize(e.location || '');
          const raceMatch = (e.races || e.event_races || []).some((r: any) =>
            normalize(r.name || '').includes(nq),
          );
          if (nTitle.includes(nq)) score += 3;
          if (nCity.includes(nq)) score += 2;
          if (nLoc.includes(nq)) score += 2;
          if (raceMatch) score += 1;
        } else if (intentActive) {
          // Pas de texte résiduel mais des intentions : on garde tous les events qui matchent
          score = 1;
        }
        return { event: e, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return new Date(a.event.event_date).getTime() - new Date(b.event.event_date).getTime();
      });
    return {
      suggestions: scored.slice(0, 6).map((x) => x.event),
      totalMatches: scored.length,
    };
  }, [events, trimmedQuery, residual, parsedSearch, intentActive]);

  // Reset selection when suggestions change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [trimmedQuery]);

  const isOpen = isFocused && trimmedQuery.length > 0;

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedIndex >= 0 && suggestions[selectedIndex]) {
      navigate(`/evenement/${suggestions[selectedIndex].slug}`);
      return;
    }
    navigate(trimmedQuery ? buildExplorerUrl(parsedSearch) : '/explorer');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Escape') {
      setIsFocused(false);
    }
  };

  const handleBlur = () => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    blurTimerRef.current = setTimeout(() => setIsFocused(false), 150);
  };

  const cancelBlur = () => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
  };

  const featured = useMemo(() => {
    const now = Date.now();
    const upcoming = events.filter((e) => new Date(e.event_date).getTime() > now);
    return upcoming.find((e) => e.hero_featured) || upcoming.find((e) => e.featured) || upcoming[0] || null;
  }, [events]);

  return (
    <section className="relative overflow-hidden bg-background pt-8 pb-0 lg:pt-10">
      {/* Diagonal speed lines */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        {[
          { top: '12%', color: 'rgba(202,255,0,0.5)', h: 2 },
          { top: '22%', color: 'rgba(255,255,255,0.05)', h: 1 },
          { top: '38%', color: 'rgba(255,45,158,0.4)', h: 2 },
          { top: '54%', color: 'rgba(0,229,255,0.3)', h: 1 },
          { top: '71%', color: 'rgba(248,255,58,0.35)', h: 2 },
          { top: '86%', color: 'rgba(255,255,255,0.08)', h: 1 },
        ].map((l, i) => (
          <div
            key={i}
            className="absolute -left-[10%] -right-[10%]"
            style={{ top: l.top, height: l.h, background: `linear-gradient(90deg, transparent, ${l.color}, transparent)`, transform: 'skewY(-4deg)' }}
          />
        ))}
      </div>

      <div className="container relative mx-auto px-4 lg:px-8">
        {/* Top meta bar */}
        <div className="flex flex-wrap justify-between items-center gap-2 border-b border-white/10 pb-5 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
          <span className="hidden sm:inline">N° 2026 · SAISON PRINTEMPS-AUTOMNE</span>
          <span className="sm:hidden">N° 2026</span>
        </div>

        {/* Main poster grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] items-center gap-10 lg:gap-0 py-12 lg:py-20">
          {/* LEFT — numbers column */}
          <div className="hidden lg:flex flex-col gap-8 xl:gap-12 order-1 min-w-0">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-primary mb-2.5">// 01</div>
              <div className="font-display italic text-4xl xl:text-6xl text-foreground leading-[0.9] -skew-x-6 whitespace-nowrap">2 400</div>
              <div className="mt-1.5 text-[11px] uppercase tracking-[0.12em] text-muted-foreground leading-tight">
                courses<br />à portée de foulée
              </div>
            </div>
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-energy mb-2.5">// 02</div>
              <div className="font-display italic text-4xl xl:text-6xl text-foreground leading-[0.9] -skew-x-6 whitespace-nowrap">
                178<span className="text-muted-foreground text-2xl xl:text-4xl align-top">K+</span>
              </div>
              <div className="mt-1.5 text-[11px] uppercase tracking-[0.12em] text-muted-foreground leading-tight">
                coureurs ont<br />épinglé leur dossard
              </div>
            </div>
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-accent mb-2.5">// 03</div>
              <div className="font-display italic text-4xl xl:text-6xl text-foreground leading-[0.9] -skew-x-6 whitespace-nowrap">
                96<span className="text-muted-foreground text-2xl xl:text-4xl align-top">%</span>
              </div>
              <div className="mt-1.5 text-[11px] uppercase tracking-[0.12em] text-muted-foreground leading-tight">
                recommandent Kourse<br />à un·e ami·e
              </div>
            </div>
          </div>

          {/* CENTER — massive title */}
          <div className="text-center lg:px-4 xl:px-8 order-2 relative min-w-0">
            <div className="inline-flex items-center gap-3.5 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.14em] text-muted-foreground/80 mb-4">
              <span className="h-px w-6 bg-muted-foreground/50" />
              LE BON DOSSARD, AU BON MOMENT
              <span className="h-px w-6 bg-muted-foreground/50" />
            </div>

            <h1 className="m-0">
              <span className="block whitespace-nowrap font-display italic uppercase text-foreground leading-[0.88] tracking-tight -skew-x-[10deg] text-[56px] sm:text-[88px] lg:text-[96px] xl:text-[140px] 2xl:text-[170px]">
                KOURS
              </span>
              <span
                className="block whitespace-nowrap font-display italic uppercase leading-[0.88] tracking-tight -skew-x-[10deg] text-[56px] sm:text-[88px] lg:text-[96px] xl:text-[140px] 2xl:text-[170px] text-transparent bg-clip-text -mt-1"
                style={{ backgroundImage: 'linear-gradient(90deg, #caff00 0%, #f8ff3a 35%, #ff7a1a 65%, #ff2d9e 100%)' }}
              >
                PLUS LOIN.
              </span>
            </h1>

            <p className="mx-auto mt-6 max-w-xl text-base lg:text-[17px] leading-relaxed text-muted-foreground">
              La plateforme de toutes les courses de France. Trails sauvages, 10K de ville, ultras mythiques — choisis, inscris-toi, pars.
            </p>

            <form onSubmit={submitSearch} className="relative mx-auto mt-8 max-w-xl">
              <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1.5">
              <label className="flex items-center gap-2 flex-1 px-4">
                <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => setIsFocused(true)}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  placeholder={animatedPlaceholder}
                  className="flex-1 bg-transparent border-0 py-3 text-[14px] text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
                  aria-autocomplete="list"
                  aria-expanded={isOpen}
                />
              </label>
              <button
                type="submit"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-3 text-[12px] font-bold uppercase tracking-[0.08em] text-primary-foreground hover:shadow-[0_0_0_8px_rgba(202,255,0,0.18)] transition-shadow"
              >
                Explorer
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
              </div>

              {isOpen && (
                <div
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={cancelBlur}
                  className="absolute left-0 right-0 top-full mt-2 z-30 overflow-hidden rounded-2xl border border-white/10 bg-[#121217]/95 backdrop-blur-md text-left shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
                >
                  {parsedSearch.intents.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 border-b border-white/5 px-4 py-2.5">
                      <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground/70">Filtres détectés</span>
                      {parsedSearch.intents.map((intent, i) => (
                        <span
                          key={`${intent.type}-${intent.value}-${i}`}
                          className="inline-flex items-center rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-primary border border-primary/30"
                        >
                          {intent.label}
                        </span>
                      ))}
                    </div>
                  )}
                  {suggestions.length === 0 ? (
                    <div className="px-4 py-6 text-center">
                      <p className="text-sm text-muted-foreground">
                        Aucune course pour <span className="text-foreground">« {trimmedQuery} »</span>
                      </p>
                      <Link
                        to="/explorer"
                        onClick={() => setIsFocused(false)}
                        className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.1em] font-bold text-primary hover:underline"
                      >
                        Voir toutes les courses <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  ) : (
                    <ul className="divide-y divide-white/5">
                      {suggestions.map((ev: any, i: number) => {
                        const races = ev.races || ev.event_races || [];
                        const distances = races
                          .map((r: any) => r.distance_km)
                          .filter(Boolean) as number[];
                        const mainDistance = distances.length
                          ? Math.max(...distances)
                          : ev.distance_km;
                        const pricedRaces = races.filter((r: any) => (r.price_cents ?? 0) > 0);
                        let minPrice = 0;
                        if (pricedRaces.length > 0) {
                          minPrice = Math.min(
                            ...pricedRaces.map((r: any) => {
                              const p = calculatePricing(
                                r.price_cents,
                                r.fees_included ?? true,
                                ev.commission_rate,
                              );
                              return p.runnerPays;
                            }),
                          );
                        } else if (ev.price_cents) {
                          minPrice = calculatePricing(
                            ev.price_cents,
                            true,
                            ev.commission_rate,
                          ).runnerPays;
                        }
                        const isSelected = i === selectedIndex;
                        return (
                          <li key={ev.id}>
                            <Link
                              to={`/evenement/${ev.slug}`}
                              onClick={() => setIsFocused(false)}
                              onMouseEnter={() => setSelectedIndex(i)}
                              className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                                isSelected ? 'bg-white/10' : 'hover:bg-white/5'
                              }`}
                            >
                              <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-white/5">
                                <img
                                  src={ev.image_url || heroImage}
                                  alt=""
                                  loading="lazy"
                                  className="h-full w-full object-cover"
                                />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-display font-bold uppercase text-[14px] tracking-tight text-foreground">
                                  <Highlight text={ev.title} query={residual} />
                                </div>
                                <div className="mt-0.5 flex items-center gap-2 text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                                  <span className="inline-flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    <Highlight text={ev.city || '—'} query={residual} />
                                  </span>
                                  <span>·</span>
                                  <span>{formatShortDate(ev.event_date)}</span>
                                  {mainDistance ? (
                                    <>
                                      <span>·</span>
                                      <span className="text-primary font-bold">
                                        {Math.round(mainDistance)} KM
                                      </span>
                                    </>
                                  ) : null}
                                </div>
                              </div>
                              {minPrice > 0 && (
                                <div className="shrink-0 font-display italic font-extrabold text-[16px] -skew-x-6 text-primary">
                                  {formatPrice(minPrice, ev.currency)}
                                </div>
                              )}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  {suggestions.length > 0 && (
                    <Link
                      to={buildExplorerUrl(parsedSearch)}
                      onClick={() => setIsFocused(false)}
                      className="flex items-center justify-between gap-2 border-t border-white/10 px-4 py-3 text-[11px] uppercase tracking-[0.1em] font-bold text-primary hover:bg-white/5"
                    >
                      <span>
                        {totalMatches > suggestions.length
                          ? `Voir les ${totalMatches} résultats`
                          : 'Voir dans l\u2019explorateur'}
                      </span>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </div>
              )}
            </form>

            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {QUICK_FILTERS.map((f) => (
                <Link
                  key={f.label}
                  to={f.to}
                  className="rounded-full border border-white/10 px-3 py-1 text-[11px] text-muted-foreground hover:border-white/30 hover:text-foreground transition-colors"
                >
                  {f.label}
                </Link>
              ))}
            </div>

            {/* Mobile stats */}
            <div className="grid grid-cols-3 gap-4 mt-10 lg:hidden">
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-primary mb-1">// 01</div>
                <div className="font-display italic text-3xl text-foreground leading-none -skew-x-6 whitespace-nowrap">2 400</div>
                <div className="mt-1 text-[9px] uppercase tracking-[0.12em] text-muted-foreground">courses</div>
              </div>
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-energy mb-1">// 02</div>
                <div className="font-display italic text-3xl text-foreground leading-none -skew-x-6 whitespace-nowrap">
                  178<span className="text-muted-foreground text-lg align-top">K+</span>
                </div>
                <div className="mt-1 text-[9px] uppercase tracking-[0.12em] text-muted-foreground">coureurs</div>
              </div>
              <div>
                <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-accent mb-1">// 03</div>
                <div className="font-display italic text-3xl text-foreground leading-none -skew-x-6 whitespace-nowrap">
                  96<span className="text-muted-foreground text-lg align-top">%</span>
                </div>
                <div className="mt-1 text-[9px] uppercase tracking-[0.12em] text-muted-foreground">recommandent</div>
              </div>
            </div>
          </div>

          {/* RIGHT — rotated featured poster */}
          <div className="hidden lg:flex justify-end order-3 min-w-0">
            {featured && (() => {
              const getTeamSize = (r: any) => {
                if (r.team_mode === 'duo') return 2;
                if (r.team_mode === 'trio') return 3;
                if (r.team_mode === 'relay') return r.team_size || 2;
                return 1;
              };
              const hasRaces = featured.races && featured.races.length > 0;
              // Capacity is already expressed in the right unit (teams for team races, runners for solo)
              const racesMax = hasRaces
                ? featured.races.reduce((sum: number, r: any) => sum + (r.max_participants || 0), 0)
                : 0;
              // Convert current_participants (always runners) into teams when relevant
              const racesCur = hasRaces
                ? featured.races.reduce((sum: number, r: any) => {
                    const ts = getTeamSize(r);
                    const cur = r.current_participants || 0;
                    return sum + (ts > 1 ? Math.floor(cur / ts) : cur);
                  }, 0)
                : 0;
              const allTeamRaces = hasRaces && featured.races.every((r: any) => getTeamSize(r) > 1);
              const maxP = racesMax > 0 ? racesMax : featured.max_participants;
              const curP = racesMax > 0 ? racesCur : (featured.current_participants ?? 0);
              const totalRunners = hasRaces
                ? featured.races.reduce((sum: number, r: any) => sum + (r.current_participants || 0), 0)
                : (featured.current_participants ?? 0);
              const pct = maxP ? Math.min(100, Math.round((curP / maxP) * 100)) : null;
              const remaining = maxP ? Math.max(0, maxP - curP) : null;
              const capacityLabel = allTeamRaces ? 'ÉQUIPES' : 'PLACES';

              let pricePerPerson = 0;
              let hasTeamRace = false;
              if (featured.races && featured.races.length > 0) {
                const perPersonPrices = featured.races
                  .filter((r: any) => r.price_cents > 0)
                  .map((r: any) => {
                    const teamSize = r.team_mode === 'duo' ? 2
                      : r.team_mode === 'trio' ? 3
                      : r.team_mode === 'relay' ? (r.team_size || 1)
                      : 1;
                    if (teamSize > 1) hasTeamRace = true;
                    const pricing = calculatePricing(r.price_cents, r.fees_included ?? true, featured.commission_rate);
                    return Math.round(pricing.runnerPays / teamSize);
                  });
                if (perPersonPrices.length > 0) pricePerPerson = Math.min(...perPersonPrices);
              } else {
                const pricing = calculatePricing(featured.price_cents, true, featured.commission_rate);
                pricePerPerson = pricing.runnerPays;
              }
              const priceLabel = formatPrice(pricePerPerson, featured.currency) + (hasTeamRace ? '/pers.' : '');

              return (
                <Link
                  to={`/evenement/${featured.slug}`}
                  className="course-du-mois relative block w-[240px] xl:w-[280px]"
                >
                  <span
                    className="course-du-mois__tag absolute -top-3 left-3.5 z-10 inline-flex items-center gap-1.5 bg-energy text-white py-1.5 pl-3 pr-[18px] text-[11px] font-bold uppercase tracking-[0.06em]"
                    style={{ clipPath: 'polygon(0 0, 100% 0, 92% 100%, 0 100%)' }}
                  >
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-75 animate-ping" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                    </span>
                    COURSE DU MOIS
                  </span>

                  <div
                    className="course-du-mois__card relative overflow-hidden rounded-2xl border border-white/15 p-[18px]"
                    style={{
                      background: '#121217',
                      boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
                    }}
                  >
                    <div
                      className="course-du-mois__photo relative aspect-[4/3] rounded-[10px] overflow-hidden"
                      style={{
                        background:
                          'radial-gradient(ellipse at 50% 30%, rgba(255,255,255,0.06), transparent 60%), repeating-linear-gradient(135deg, #1a1a22 0 8px, #14141a 8px 16px)',
                      }}
                    >
                      {(featured.image_url || heroImage) && (
                        <img
                          src={featured.image_url || heroImage}
                          alt={featured.title}
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover"
                        />
                      )}
                      <span
                        aria-hidden
                        className="absolute inset-0 pointer-events-none"
                        style={{ background: 'linear-gradient(180deg, transparent 40%, rgba(10,10,12,0.8))' }}
                      />
                      {featured.distance_km && (
                        <div
                          className="absolute top-0 left-0 py-1.5 pl-3 pr-[22px] font-display italic font-extrabold text-base tracking-[0.02em]"
                          style={{
                            background: '#f8ff3a',
                            color: '#0a0a0c',
                            clipPath: 'polygon(0 0, 100% 0, 90% 100%, 0 100%)',
                          }}
                        >
                          {Math.round(featured.distance_km)} KM
                        </div>
                      )}
                    </div>

                    <div className="mt-3.5">
                      <div className="font-display font-extrabold uppercase text-[20px] leading-[0.95] tracking-[-0.01em] text-foreground line-clamp-2">
                        {featured.title}
                      </div>
                      <div className="mt-1.5 text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
                        {formatShortDate(featured.event_date)}
                        {featured.city ? ` · ${featured.city.toUpperCase()}` : ''}
                        {totalRunners ? ` · ${totalRunners} INSCRITS` : ''}
                      </div>

                      {pct !== null && (
                        <>
                          <div className="mt-3 h-1 rounded-sm overflow-hidden bg-white/[0.06]">
                            <div className="course-du-mois__fill h-full bg-energy" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="mt-1.5 text-[10px] uppercase tracking-[0.08em] text-energy">
                            {pct}% REMPLI{remaining !== null && remaining > 0 ? ` · ${remaining} ${capacityLabel}` : ''}
                          </div>
                        </>
                      )}

                      <div className="mt-3.5 pt-3.5 border-t border-white/10 flex items-center justify-between">
                        <span className="font-display italic font-extrabold text-[26px] leading-none text-primary -skew-x-6">
                          {priceLabel}
                        </span>
                        <span className="course-du-mois__cta inline-flex items-center rounded-full bg-foreground text-background px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.1em]">
                          Réserver
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })()}
          </div>
        </div>

        <div className="hidden lg:flex flex-col items-center gap-2.5 pb-10 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/60">
          <span>SCROLL POUR EXPLORER</span>
          <span className="h-10 w-px bg-gradient-to-b from-muted-foreground/60 to-transparent" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
